import { FC, ReactElement, useContext, useEffect, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useUpphovsman } from 'hooks/useUpphovsman';

import ErrorBoundary from 'components/errorBoundary/ErrorBoundary';
import { TextButton } from 'components/sharedElements/sharedElements';
import HandelseModal from 'components/utkast/handelseModal/HandelseModal';
import UpphovsmanInfoDialog from 'components/utkast/upphovsmanInfoDialog/UpphovsmanInfoDialog';
import { HandelseFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';
import { UpphovsmanContext } from 'components/utkast/utkastRegistration/UtkastRegistration';

import { HandelseTyp } from 'models';

import { HandelseTypEnum } from 'enums';

import { sortHandelserFormValues } from 'utilities/sortUtility';
import { v4 as uuid } from 'uuid';

const getHandelseTyp = (handelseTyp: HandelseTypEnum, handelsetyper: HandelseTyp[]): HandelseTyp => {
  const found = handelsetyper.find((h) => h.handelsetyp === handelseTyp);
  if (found) {
    return found;
  } else throw new Error(`Okänd Händelsetyp ${handelseTyp}`);
};

const getDateringText = (handelse: HandelseFormValues): string => {
  if (!handelse.datering_kand) return ', okänt årtal';

  if (
    (handelse.datering_from && !handelse.datering_tom) ||
    (handelse.datering_from && handelse.datering_from === handelse.datering_tom)
  ) {
    return `, klar ${handelse.datering_from}`;
  } else if (handelse.datering_from && handelse.datering_tom) {
    return `, klar ca ${handelse.datering_from}–${handelse.datering_tom}`;
  }

  return '';
};

interface HandelseListItemProps {
  handelse: HandelseFormValues;
  handelsetyper: Array<HandelseTyp>;
  onSubmit: (values: HandelseFormValues) => void;
  onErase: () => void;
}

const HandelseListItem: FC<HandelseListItemProps> = ({ handelse, handelsetyper, onSubmit, onErase }) => {
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);
  const { setSelectedUpphovsman, selectedUpphovsman } = useContext(UpphovsmanContext);
  const [tagList] = useUpphovsman(handelse.upphovsman, selectedUpphovsman, domaner);
  const { setDialog, unsetDialog } = useDialog();

  const [handelseCopy, setHandelseCopy] = useState(JSON.parse(JSON.stringify(handelse)));

  useEffect(() => {
    setHandelseCopy(JSON.parse(JSON.stringify(handelse)));
  }, [setHandelseCopy, handelse]);

  return (
    <>
      {domaner && (
        <li className="tw-mb-4 tw-flex tw-flex-col">
          <div className="tw-flex tw-justify-between">
            <span className="tw-font-semibold">
              {getHandelseTyp(handelse.handelsetyp, domaner.handelsetyper).namn}
              {getDateringText(handelse)}
            </span>

            <TextButton
              icon="raa-icon-edit"
              onClick={() =>
                setDialog(HandelseModal, {
                  handelse: handelseCopy,
                  handelsetyper: handelsetyper,
                  onCancel: () => setHandelseCopy(JSON.parse(JSON.stringify(handelse))),
                  onErase,
                  onHandelseChange: setHandelseCopy,
                  onSubmit,
                  selectedUpphovsman,
                  setSelectedUpphovsman
                })
              }
            >
              Ändra
            </TextButton>
          </div>
          <p className="dynamic-word-break">{handelse.kommentar}</p>
          <ul className="tw-mt-1">
            {tagList.map((tag, index) => (
              <li className="tw-flex tw-text-size-0.813" key={index}>
                <span className="tw-text-raa-gray-5">{`${tag.role.namn}: `}</span>
                <TextButton
                  onClick={() => setDialog(UpphovsmanInfoDialog, { unsetDialog, id: tag.upphovsman.upphovsman_id })}
                >
                  {tag.displayName}
                </TextButton>
              </li>
            ))}
          </ul>
        </li>
      )}
    </>
  );
};

interface HandelseListProps {
  handelserValues: Array<HandelseFormValues>;
  excludeType?: Array<HandelseTypEnum>;
  handelseName?: string;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  maxCount?: number;
}

const HandelseList: FC<HandelseListProps> = ({
  handelserValues,
  setFieldValue,
  excludeType = [],
  handelseName = 'händelse',
  maxCount
}): ReactElement => {
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);
  const { setDialog } = useDialog();

  const getDefaultHandelseTyp = (): HandelseTypEnum => {
    const handelsetyper = domaner?.handelsetyper.map((h) => h.handelsetyp).filter((h) => !excludeType.includes(h));
    return handelsetyper?.length === 1 ? handelsetyper[0] : ('' as HandelseTypEnum);
  };

  const { setSelectedUpphovsman, selectedUpphovsman } = useContext(UpphovsmanContext);

  const getNewHandelseCopy = (): HandelseFormValues => ({
    attribut_id: uuid(),
    handelsetyp: getDefaultHandelseTyp(),
    upphovsman: [],
    datering_kand: 'true',
    datering_from: '',
    datering_tom: '',
    kommentar: ''
  });

  const [newHandelseCopy, setNewHandelseCopy] = useState<HandelseFormValues>(getNewHandelseCopy());

  const addNewHandelse = () => {
    setDialog(HandelseModal, {
      selectedUpphovsman: selectedUpphovsman,
      setSelectedUpphovsman: setSelectedUpphovsman,
      handelse: newHandelseCopy,
      handelsetyper: domaner?.handelsetyper.filter((h) => !excludeType.includes(h.handelsetyp)) ?? [],
      onHandelseChange: setNewHandelseCopy,
      onCancel: () => setNewHandelseCopy(getNewHandelseCopy()),
      onSubmit: (handelseFormValue: HandelseFormValues) => {
        setNewHandelseCopy(getNewHandelseCopy);
        setFieldValue(
          'handelser',
          sortHandelserFormValues([...handelserValues, handelseFormValue], domaner?.handelsetyper)
        );
      }
    });
  };

  useEffect(() => {
    setFieldValue('handelser', sortHandelserFormValues(handelserValues, domaner?.handelsetyper), false);
  }, [domaner, handelserValues, setFieldValue]);

  return (
    <>
      {domaner && (
        <>
          {handelserValues.length > 0 && (
            <ul className="tw-mb-4">
              {handelserValues.map((handelse, index) => (
                <ErrorBoundary key={`handelse-list-${index}`}>
                  {!excludeType.includes(handelse.handelsetyp) && (
                    <HandelseListItem
                      handelse={handelse}
                      handelsetyper={domaner?.handelsetyper.filter((h) => !excludeType.includes(h.handelsetyp)) ?? []}
                      onErase={() => {
                        setFieldValue(
                          'handelser',
                          handelserValues.filter((_, i) => i !== index)
                        );
                      }}
                      onSubmit={(newValue: HandelseFormValues) => {
                        setFieldValue(
                          'handelser',
                          sortHandelserFormValues(
                            handelserValues.map((original, i) => (i === index ? newValue : original))
                          ),
                          false
                        );
                      }}
                    />
                  )}
                </ErrorBoundary>
              ))}
            </ul>
          )}
          {(!maxCount || handelserValues.length < maxCount) && (
            <TextButton icon="raa-icon-add" onClick={addNewHandelse}>
              Lägg till {handelseName}
            </TextButton>
          )}
        </>
      )}
    </>
  );
};

export default HandelseList;
